/* eslint-disable @typescript-eslint/ban-ts-comment */
import FileDragAndDrop from '@/src/components/common/FileDragAndDrop';
import MarkDownPreviewEditor from '@/src/components/common/MarkdownPreviewEditor/MarkDownPreviewEditor';
import useInputBuffer from '@/src/hooks/useInputBuffer';
import { config } from '@/src/config/app-config';
import { useFlashbars } from '@/src/store/flashbar.context';
import CodeView from '@amzn/awsui-code-view/code-view';
import javascriptHighlight from '@amzn/awsui-code-view/highlight/javascript';
import pythonHighlight from '@amzn/awsui-code-view/highlight/python';
import {
  Box,
  Button,
  ExpandableSection,
  FormField,
  Grid,
  HelpPanel,
  Icon,
  Input,
  Link,
  Multiselect,
  RadioGroup,
  Select,
  SelectProps,
  SpaceBetween,
  Spinner,
  TextContent,
  Textarea,
  Toggle,
} from '@amzn/awsui-components-react';
import { OptionDefinition } from '@amzn/awsui-components-react/polaris/internal/components/option/interfaces';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useChallenges } from '../../../../store/challenge.context';
import { useToolPanel } from '../../../../store/tool-panel.context';
import {
  ChallengePropAction,
  ChallengeTaskFields,
  useCreateChallenge,
} from '../../../../store/create-challenge.context';
import {
  Challenge,
  ChallengeTask,
  ChallengeTaskValidationFnRuntimes,
  ChallengeTaskValidationType,
  SUPPORTED_VALIDATION_RUNTIMES,
} from '../../../../types/Challenge';
import { i18nKeys } from '../../../../utils/i18n.utils';
import { preProdLogger } from '../../../../utils/log.utils';
import { encodeAngleBrackets, safeString } from '../../../../utils/string.utils';
import { Column } from '../../../common/Column';
import { Columns } from '../../../common/Columns';
import { ConfirmModal } from '../../../common/ConfirmModal';
import { KeyValue } from '../../../common/KeyValue';
import { Markdown } from '../../../common/Markdown';
import JamCodeEditor from '../../challengesCommon/JamCodeEditor';
import ChallengeCluesDetail from './Clues';

interface ChallengeTaskDetailProps {
  challenge: Challenge;
  task: ChallengeTask;
  getTaskLabel?: (taskId: string) => string;
  removeTask: (challengeId: string, challengeVersion: number, taskId: string) => Promise<void>;
  updateDebounceDelay?: number;
}

const ChallengeTaskDetail: React.FC<ChallengeTaskDetailProps> = ({
  challenge,
  task,
  removeTask,
  updateDebounceDelay = 500,
}) => {
  const { t } = useTranslation();
  const { addErrorFlashbar } = useFlashbars();
  const { editMode, editedChallenge, handleUpdateChallengeTask, challengeTaskValidator } = useCreateChallenge();
  const { challengeConfig, getChallengeConfiguration } = useChallenges();
  const { renderToolPanelContent, toggleShowToolPanel, toggleToolPanel, setToolPanelWidth } = useToolPanel();

  const openVariableScoringHelpPanel = () => {
    setToolPanelWidth(400);
    toggleShowToolPanel(true);
    toggleToolPanel(true);
    renderToolPanelContent(
      <HelpPanel header={<h2>{t(i18nKeys.challenges.challengeDetails.titles.variableScoring)}</h2>}>
        <SpaceBetween size="m">
          <Box variant="p">{t(i18nKeys.challenges.challengeDetails.variableScoringHelp.intro)}</Box>
          <div>
            <Box variant="h4">{t(i18nKeys.challenges.challengeDetails.variableScoringHelp.howItWorksTitle)}</Box>
            <Box variant="p">{t(i18nKeys.challenges.challengeDetails.variableScoringHelp.howItWorks)}</Box>
          </div>
          <div>
            <Box variant="h4">{t(i18nKeys.challenges.challengeDetails.variableScoringHelp.formulaTitle)}</Box>
            <Box variant="p">
              <span style={{ fontFamily: 'monospace' }}>
                {t(i18nKeys.challenges.challengeDetails.variableScoringHelp.formula)}
              </span>
            </Box>
            <Box variant="p">{t(i18nKeys.challenges.challengeDetails.variableScoringHelp.formulaExample)}</Box>
          </div>
          <div>
            <Box variant="h4">{t(i18nKeys.challenges.challengeDetails.variableScoringHelp.monotonicTitle)}</Box>
            <Box variant="p">{t(i18nKeys.challenges.challengeDetails.variableScoringHelp.monotonic)}</Box>
          </div>
          <div>
            <Box variant="h4">{t(i18nKeys.challenges.challengeDetails.variableScoringHelp.requirementsTitle)}</Box>
            <Box variant="p">{t(i18nKeys.challenges.challengeDetails.variableScoringHelp.requirements)}</Box>
          </div>
        </SpaceBetween>
      </HelpPanel>
    );
  };

  const getTaskLabel = (taskId: string): string => {
    const foundTask = challenge && challenge.props.tasks.find((currTask: ChallengeTask) => currTask.id === taskId);
    return foundTask ? foundTask.label : t(i18nKeys.challenges.challengeDetails.text.unknownTask);
  };

  const [challengeTask, setChallengeTask] = useState<ChallengeTask | undefined>(task);

  const [showSampleFunction, setShowSampleFunction] = useState(false);
  const [visibleConfirmSample, setVisibleConfirmSample] = useState(false);
  const [visibleRemoveTaskConfirm, visibleRemoveTaskConfirmSet] = useState(false);

  const field = i18nKeys.challenges.subSections.tasks;
  const [weightedScoreErrorText, setWeightedScoreErrorText] = useState<string>('');
  const [titleErrorText, setTitleErrorText] = useState<string>('');
  const [globalStaticAnswerErrorText, setGlobalStaticAnswerErrorText] = useState<string>('');
  const [taskValidationFunctionErrorText, setTaskValidationFunctionErrorText] = useState<string>('');
  const [isLoading, isLoadingSet] = useState(false);
  const [selectedTaskPrerequisites, setSelectedTaskPrerequisites] = useState<readonly OptionDefinition[]>([]);

  useEffect(() => {
    // reset error fields when editMode is changed
    if (!editMode) {
      setWeightedScoreErrorText('');
      setTitleErrorText('');
      setGlobalStaticAnswerErrorText('');
      setTaskValidationFunctionErrorText('');
      if (challengeTask) {
        // When not in edit mode (display mode), decode all entities including angle brackets
        challengeTask.decodeForMarkdown(false);
      }
    } else {
      if (challengeTask) {
        // When in edit mode, preserve angle bracket entities to prevent round-trip issues
        challengeTask.decodeForMarkdown(true);
      }
      if (dependencyPrerequisiteOptions.length) {
        setSelectedTaskPrerequisites(
          dependencyPrerequisiteOptions.filter((ta) => challengeTask?.dependsOnTaskIds.includes(ta.value || ''))
        );
      }
    }
  }, [editMode]);

  useEffect(() => {
    getChallengeConfiguration();
    const taskHasLambdaValidation = [
      ChallengeTaskValidationType.LAMBDA_FUNCTION,
      ChallengeTaskValidationType.LAMBDA_FUNCTION_WITH_INPUT,
    ].includes(task?.validationType || '');
    const validationRuntimeNotSupported =
      task?.validationFunctionRuntime &&
      !SUPPORTED_VALIDATION_RUNTIMES.includes(task.validationFunctionRuntime as ChallengeTaskValidationFnRuntimes);
    if (taskHasLambdaValidation && validationRuntimeNotSupported) {
      const language = task.validationFunctionRuntime.includes('python') ? 'Python' : 'NodeJS';
      addErrorFlashbar(
        t(i18nKeys.challenges.challengeDetails.messages.validationLambdaDeprecatedRuntimeMessage, { language }),
        t(i18nKeys.challenges.challengeDetails.messages.validationLambdaDeprecatedRuntimeHeading, {
          taskNumber: task?.taskNumber,
        })
      );
    }
  }, []);

  useEffect(() => {
    if (editedChallenge) {
      setChallengeTask(editedChallenge.props.tasks.find((ct) => ct.id === task.id));
    }
  }, [editedChallenge]);

  const validateScoreOnCommit = useRef(false);
  const committedTask = editedChallenge?.props?.tasks?.find((ct) => ct.id === task?.id);
  useEffect(() => {
    // Validate after the async commit lands (keyed on committed value), only
    // when a blur flagged it — not on mount. Inline-on-blur reads a stale model.
    if (editMode && validateScoreOnCommit.current && committedTask) {
      validateScoreOnCommit.current = false;
      challengeTaskValidator(
        committedTask,
        new Map<ChallengeTaskFields, (error: string) => void>([
          [ChallengeTaskFields.WEIGHTED_SCORE, (error: string) => setWeightedScoreErrorText(error)],
        ])
      ).isValidField(ChallengeTaskFields.WEIGHTED_SCORE);
    }
  }, [committedTask?.scorePercent]);

  const supportedRuntimeOptions = useMemo(() => {
    const runtimes: SelectProps.Option[] = [];
    const allowed = task?.validationFunctionRuntime
      ? [...new Set([...SUPPORTED_VALIDATION_RUNTIMES, task.validationFunctionRuntime])]
      : SUPPORTED_VALIDATION_RUNTIMES;
    if (challengeConfig) {
      challengeConfig.supportedLambdaRuntimes
        .filter((runtime) => allowed.includes(runtime))
        .forEach((runtime) => runtimes.push({ label: runtime, value: runtime }));
    }
    return runtimes.sort((a, b) => (a?.value || '').localeCompare(b?.value || ''));
  }, [challengeConfig, task?.validationFunctionRuntime]);

  const isFirstTask = useMemo(() => task?.taskNumber < 2, [task]);

  const weightedScoreInput = useInputBuffer(challengeTask?.scorePercent, (inputValue) =>
    handleUpdateChallengeTask(ChallengePropAction.TASK_WEIGHTED_SCORE, challengeTask?.taskNumber || 0, inputValue)
  );

  if (!task) {
    return null;
  }

  const taskValidator = challengeTaskValidator(
    challengeTask || task,
    new Map<ChallengeTaskFields, (error: string) => void>([
      [ChallengeTaskFields.WEIGHTED_SCORE, (error: string) => setWeightedScoreErrorText(error)],
      [ChallengeTaskFields.TASK_TITLE, (error: string) => setTitleErrorText(error)],
      [ChallengeTaskFields.GLOBAL_STATIC_ANSWER, (error: string) => setGlobalStaticAnswerErrorText(error)],
      [ChallengeTaskFields.VALIDATION_FUNCTION, (error: string) => setTaskValidationFunctionErrorText(error)],
    ])
  );

  const handleFileUpload = (uploadedFile: File) => {
    // Handle file upload
    uploadedFile
      .text()
      .then((file) => {
        handleUpdateChallengeTask(ChallengePropAction.TASK_VALIDATION_LAMBDA, task.taskNumber, file);
      })
      .catch((err) => preProdLogger(err));
  };

  const runtimeProps = useMemo(() => {
    if (challengeTask?.validationFunctionRuntime && challengeTask.validationFunctionRuntime.includes('python')) {
      return {
        codeHighlight: pythonHighlight,
        fileExtension: 'py',
        language: 'python',
        acceptedMimeTypes: [
          'text/x-python',
          'text/x-python-code',
          'text/x-python-script',
          'application/x-python-code',
          'application/x-python-script',
        ],
        sampleFunction: safeString(challengeConfig?.pythonSampleLambdaCode),
      };
    }
    return {
      codeHighlight: javascriptHighlight,
      fileExtension: 'js',
      language: 'javascript',
      acceptedMimeTypes: ['text/javascript', 'application/javascript', 'application/x-javascript', 'text/ecmascript'],
      sampleFunction: safeString(challengeConfig?.nodeSampleLambdaCode),
    };
  }, [challengeTask?.validationFunctionRuntime, challengeConfig]);

  const title = task.title ? task.title : t(i18nKeys.challenges.challengeDetails.text.challengeTaskEmptyTitle);

  const taskValidationFunctionFileName = challengeTask?.validationFunction
    ? `${editedChallenge?.challengeId}-task-${challengeTask.taskNumber}-validation-function.${runtimeProps.fileExtension}`
    : '';

  const getValidationType = () => {
    switch (task.validationType) {
      case ChallengeTaskValidationType.GLOBAL_STATIC_ANSWER:
        return t(
          i18nKeys.challenges.subSections.tasks.subSections.fields.validationType.types.globalStaticAnswer.label
        );
      case ChallengeTaskValidationType.CFN_OUTPUT_PARAMETER:
        return t(i18nKeys.challenges.subSections.tasks.subSections.fields.validationType.types.dynamicAnswer.label);
      case ChallengeTaskValidationType.LAMBDA_FUNCTION:
        return t(i18nKeys.challenges.subSections.tasks.subSections.fields.validationType.types.lambdaFunction.label);
      case ChallengeTaskValidationType.LAMBDA_FUNCTION_WITH_INPUT:
        return t(
          i18nKeys.challenges.subSections.tasks.subSections.fields.validationType.types.lambdaFunctionInput.label
        );
    }
  };

  const dependencyPrerequisiteOptions = useMemo(
    () =>
      Object.values(
        challenge?.props?.tasks.reduce((accum, curr, currIndex) => {
          if (curr?.id && curr?.id !== challengeTask?.id) {
            accum[curr?.taskNumber || currIndex] = { label: curr.label, value: curr.id };
          }
          return accum;
        }, {} as { [key: number]: OptionDefinition })
      ),
    [challenge?.props]
  );

  return (
    <div style={{ marginBottom: '1.5rem' }}>
      <ExpandableSection
        variant="container"
        headerActions={
          editMode && task.taskNumber > 1 ? (
            <Button
              variant="icon"
              iconName="close"
              ariaLabel="Remove Task"
              onClick={() => visibleRemoveTaskConfirmSet(true)}
              data-testid="remove-task-button"
            />
          ) : null
        }
        headerText={`${t(i18nKeys.challenges.challengeDetails.headings.task, { count: task.taskNumber })}: ${title}`}
        data-testid={`task-${task.taskNumber}-expandable`}
      >
        <SpaceBetween size={'s'} direction="vertical">
          <div className="task-section">
            <Columns columns={2} variant="default">
              <Column size={'s'}>
                <KeyValue
                  className="primary-text"
                  label={
                    <TextContent>
                      <p>
                        <strong>{t(i18nKeys.challenges.challengeDetails.titles.weightedScore)}</strong>
                      </p>
                    </TextContent>
                  }
                >
                  {!editMode ? (
                    `${task.scorePercent}%`
                  ) : (
                    <FormField
                      constraintText={t(field.subSections.fields.weightedScore.constraint)}
                      errorText={weightedScoreErrorText}
                    >
                      <Input
                        value={weightedScoreInput.value}
                        data-testid={`challenge-task-${task.taskNumber}__weighted-score-input`}
                        onBlur={() => {
                          const typed = weightedScoreInput.value;
                          const willChange = typed.trim() !== '' && Number(typed) !== challengeTask?.scorePercent;
                          validateScoreOnCommit.current = willChange;
                          weightedScoreInput.handleBlur();
                          if (!willChange) {
                            // Model is already current; validate inline (no staleness).
                            taskValidator.isValidField(ChallengeTaskFields.WEIGHTED_SCORE);
                          }
                        }}
                        onChange={({ detail }) => weightedScoreInput.handleChange(detail.value)}
                        placeholder={t(
                          i18nKeys.challenges.subSections.tasks.subSections.fields.weightedScore.placeholder
                        )}
                      />
                    </FormField>
                  )}
                </KeyValue>
              </Column>
              {!isFirstTask && (
                <Column size="s">
                  <KeyValue
                    className="primary-text"
                    label={
                      <TextContent>
                        <p>
                          <strong>{t(i18nKeys.challenges.challengeDetails.titles.prerequisites)}</strong>
                        </p>
                      </TextContent>
                    }
                  >
                    {!editMode &&
                      task.dependsOnTaskIds.length > 0 &&
                      task.dependsOnTaskIds.map((id, i) => {
                        return <div key={i}>{getTaskLabel(id)}</div>;
                      })}
                    {!editMode && task.dependsOnTaskIds.length === 0 && t(i18nKeys.general.none)}
                    {editMode && (
                      <>
                        <SpaceBetween size={'s'}>
                          <FormField constraintText={t(i18nKeys.challenges.challengeDetails.titles.enterTaskId)}>
                            <Multiselect
                              selectedOptions={selectedTaskPrerequisites}
                              data-testid={`challenge-task-${task.taskNumber}__dependency-multiselect`}
                              onChange={({ detail }) => {
                                setSelectedTaskPrerequisites(detail.selectedOptions);
                                handleUpdateChallengeTask(
                                  ChallengePropAction.TASK_PREREQUISITES,
                                  challengeTask?.taskNumber || 0,
                                  detail.selectedOptions.reduce((acc, curr) => {
                                    if (!!curr?.value) {
                                      acc.push(curr.value);
                                    }
                                    return acc;
                                  }, [] as string[])
                                );
                              }}
                              options={dependencyPrerequisiteOptions}
                            />
                          </FormField>
                        </SpaceBetween>
                      </>
                    )}
                  </KeyValue>
                </Column>
              )}
              <Column size={'s'}>
                <KeyValue
                  className="primary-text"
                  label={
                    <TextContent>
                      <p>
                        <strong>{t(i18nKeys.challenges.challengeDetails.titles.validationType)}</strong>
                      </p>
                    </TextContent>
                  }
                >
                  {!editMode ? (
                    getValidationType()
                  ) : (
                    <RadioGroup
                      items={[
                        {
                          value: ChallengeTaskValidationType.LAMBDA_FUNCTION,
                          label: t(
                            i18nKeys.challenges.subSections.tasks.subSections.fields.validationType.types.lambdaFunction
                              .label
                          ),
                          description: t(
                            i18nKeys.challenges.subSections.tasks.subSections.fields.validationType.types.lambdaFunction
                              .description
                          ),
                        },
                        {
                          value: ChallengeTaskValidationType.LAMBDA_FUNCTION_WITH_INPUT,
                          label: t(
                            i18nKeys.challenges.subSections.tasks.subSections.fields.validationType.types
                              .lambdaFunctionInput.label
                          ),
                          description: t(
                            i18nKeys.challenges.subSections.tasks.subSections.fields.validationType.types
                              .lambdaFunctionInput.description
                          ),
                        },
                        {
                          value: ChallengeTaskValidationType.CFN_OUTPUT_PARAMETER,
                          label: t(
                            i18nKeys.challenges.subSections.tasks.subSections.fields.validationType.types.dynamicAnswer
                              .label
                          ),
                          description: t(
                            i18nKeys.challenges.subSections.tasks.subSections.fields.validationType.types.dynamicAnswer
                              .description
                          ),
                        },
                        {
                          value: ChallengeTaskValidationType.GLOBAL_STATIC_ANSWER,
                          label: t(
                            i18nKeys.challenges.subSections.tasks.subSections.fields.validationType.types
                              .globalStaticAnswer.label
                          ),
                          description: t(
                            i18nKeys.challenges.subSections.tasks.subSections.fields.validationType.types
                              .globalStaticAnswer.description
                          ),
                        },
                      ]}
                      value={challengeTask?.validationType || ''}
                      onChange={({ detail }) =>
                        handleUpdateChallengeTask(
                          ChallengePropAction.TASK_VALIDATION_TYPE,
                          challengeTask?.taskNumber || 0,
                          detail.value
                        )
                      }
                      data-testid={`task-${task.taskNumber}-validation-type`}
                    />
                  )}
                </KeyValue>
              </Column>
            </Columns>
            {config.ff.enableVariableScoring && challengeTask?.validatedByLambda && (
              <KeyValue
                className="primary-text"
                label={
                  <TextContent>
                    <p>
                      <strong>{t(i18nKeys.challenges.challengeDetails.titles.variableScoring)}</strong>{' '}
                      <Link variant="info" onFollow={openVariableScoringHelpPanel}>
                        {t(i18nKeys.general.info)}
                      </Link>
                    </p>
                  </TextContent>
                }
              >
                {!editMode ? (
                  task.variableScoringEnabled ? (
                    t(i18nKeys.general.enabled)
                  ) : (
                    t(i18nKeys.general.disabled)
                  )
                ) : (
                  <Toggle
                    checked={challengeTask?.variableScoringEnabled || false}
                    data-testid={`challenge-task-${task.taskNumber}__variable-scoring-toggle`}
                    onChange={({ detail }) =>
                      handleUpdateChallengeTask(
                        ChallengePropAction.TASK_VARIABLE_SCORING_ENABLED,
                        challengeTask?.taskNumber || 0,
                        detail.checked
                      )
                    }
                  >
                    {t(i18nKeys.challenges.challengeDetails.titles.variableScoringDescription)}
                  </Toggle>
                )}
              </KeyValue>
            )}
            <div>
              <ExpandableSection
                variant="container"
                className="tasks-container"
                headerText={t(i18nKeys.challenges.challengeDetails.headings.content)}
                data-testid={`task-${task.taskNumber}-content-expandable`}
              >
                <div>
                  <KeyValue
                    className="primary-text"
                    label={
                      <TextContent>
                        <p>
                          <strong>{t(i18nKeys.challenges.challengeDetails.titles.title)}</strong>
                        </p>
                      </TextContent>
                    }
                  >
                    {!editMode ? (
                      title
                    ) : (
                      <FormField
                        constraintText={`${t(field.subSections.fields.title.constraint)} ${t(
                          i18nKeys.general.avoidPIIWarning
                        )}`}
                        errorText={titleErrorText}
                      >
                        <Input
                          onBlur={() => {
                            taskValidator.isValidField(ChallengeTaskFields.TASK_TITLE);
                          }}
                          data-testid={`challenge-task-${task.taskNumber}__title-input`}
                          onChange={({ detail }) =>
                            handleUpdateChallengeTask(
                              ChallengePropAction.TASK_TITLE,
                              challengeTask?.taskNumber || 0,
                              detail.value
                            )
                          }
                          value={challengeTask?.title || ''}
                        />
                      </FormField>
                    )}
                  </KeyValue>
                  <Box>
                    {!editMode ? (
                      <Markdown content={task.content || ''} />
                    ) : (
                      <KeyValue
                        className="primary-text"
                        label={
                          <TextContent>
                            <p>
                              <strong>{t(i18nKeys.challenges.challengeDetails.headings.content)}</strong>
                            </p>
                            <p>
                              <small>{t(i18nKeys.general.avoidPIIWarning)}</small>
                            </p>
                          </TextContent>
                        }
                      >
                        <Box margin={{ top: 's' }}>
                          <MarkDownPreviewEditor
                            data-testid={`challenge-task-${task.taskNumber}__content-editor`}
                            updateDebounceDelay={updateDebounceDelay}
                            text={encodeAngleBrackets(challengeTask?.content || task.content || '')}
                            onTextChanged={(text) =>
                              handleUpdateChallengeTask(
                                ChallengePropAction.TASK_CONTENT as string,
                                challengeTask?.taskNumber || 0,
                                text
                              )
                            }
                          />
                        </Box>
                      </KeyValue>
                    )}
                  </Box>
                </div>
              </ExpandableSection>
            </div>
            <ChallengeCluesDetail taskId={task.id || ''} taskNumber={task.taskNumber} />
            {challengeTask && (
              <div className="dynamicValidationArea">
                {challengeTask.validationType !== ChallengeTaskValidationType.GLOBAL_STATIC_ANSWER && (
                  <Box margin={{ top: 'l' }}>
                    <ExpandableSection
                      variant="container"
                      headerText={t(i18nKeys.challenges.challengeDetails.headings.validationFunction)}
                      data-testid={`task-${task.taskNumber}-validation-function-expandable`}
                    >
                      {editMode && (
                        <SpaceBetween size={'s'}>
                          <Grid gridDefinition={[{ colspan: 4 }]}>
                            {challengeConfig && (
                              <Select
                                selectedOption={
                                  supportedRuntimeOptions.find(
                                    (runtime: OptionDefinition) =>
                                      runtime.value === challengeTask.validationFunctionRuntime
                                  ) || null
                                }
                                options={supportedRuntimeOptions}
                                data-testid={`challenge-task-${task.taskNumber}__runtime`}
                                onChange={({ detail }) =>
                                  handleUpdateChallengeTask(
                                    ChallengePropAction.TASK_VALIDATION_RUNTIME,
                                    task.taskNumber,
                                    detail.selectedOption.value
                                  )
                                }
                              />
                            )}
                          </Grid>
                          <SpaceBetween size="s">
                            {challengeTask.validationFunction && (
                              <Box textAlign="center">
                                <Icon name="file" />
                                <Link href="#" variant="primary">
                                  {taskValidationFunctionFileName}
                                </Link>
                              </Box>
                            )}
                            <FileDragAndDrop
                              onUpdate={(files) => handleFileUpload(files[0])}
                              validMimes={runtimeProps.acceptedMimeTypes}
                              data-testid={`challenge-task-${task.taskNumber}__clues-upload`}
                              constraintText={t(i18nKeys.challenges.challengeDetails.text.uploadFileOrDrop)}
                            />
                          </SpaceBetween>
                          <SpaceBetween size="m" direction="horizontal">
                            {showSampleFunction ? (
                              <Button
                                data-testid={`challenge-task-${task.taskNumber}__clues-hide-sample`}
                                onClick={() => setShowSampleFunction(false)}
                              >
                                {t(i18nKeys.challenges.challengeDetails.buttons.hideSampleFunction)}
                              </Button>
                            ) : (
                              <Button
                                data-testid={`challenge-task-${task.taskNumber}__clues-show-sample`}
                                onClick={() => setShowSampleFunction(true)}
                              >
                                {t(i18nKeys.challenges.challengeDetails.buttons.showSampleFunction)}
                              </Button>
                            )}
                            <Button
                              data-testid={`challenge-task-${task.taskNumber}__clues-use-sample`}
                              onClick={() => setVisibleConfirmSample(true)}
                            >
                              {t(i18nKeys.challenges.challengeDetails.buttons.useSampleFunction)}
                            </Button>
                          </SpaceBetween>
                          {showSampleFunction && challengeConfig && (
                            <CodeView
                              data-testid={`challenge-task-${task.taskNumber}__sample-validation-fn-codeview`}
                              highlight={runtimeProps.codeHighlight}
                              content={runtimeProps.sampleFunction}
                            />
                          )}
                          <FormField errorText={taskValidationFunctionErrorText}>
                            <JamCodeEditor
                              language={runtimeProps.language}
                              value={challengeTask.validationFunction || ''}
                              data-testid={`challenge-task-${task.taskNumber}__lambda-validation-editor`}
                              setValue={(e) => {
                                handleUpdateChallengeTask(
                                  ChallengePropAction.TASK_VALIDATION_LAMBDA,
                                  task.taskNumber,
                                  e
                                );
                              }}
                            />
                          </FormField>
                        </SpaceBetween>
                      )}
                      {!editMode && !!challengeTask.validationFunction && (
                        <>
                          <KeyValue bold label={t(i18nKeys.challenges.challengeDetails.titles.lambdaRuntime)}>
                            {challengeTask.validationFunctionRuntime}
                          </KeyValue>
                          <CodeView content={challengeTask.validationFunction} highlight={runtimeProps.codeHighlight} />
                        </>
                      )}
                    </ExpandableSection>
                  </Box>
                )}
                {challengeTask.validationType === ChallengeTaskValidationType.GLOBAL_STATIC_ANSWER && (
                  <Box margin={{ top: 'l' }}>
                    <ExpandableSection
                      variant="container"
                      headerText={t(i18nKeys.challenges.challengeDetails.headings.globalStaticAnswer)}
                      data-testid={`task-${task.taskNumber}-global-static-expandable`}
                    >
                      {!editMode ? (
                        challengeTask.globalStaticAnswer
                      ) : (
                        <FormField errorText={globalStaticAnswerErrorText}>
                          <Textarea
                            onBlur={() => {
                              taskValidator.isValidField(ChallengeTaskFields.GLOBAL_STATIC_ANSWER);
                            }}
                            value={challengeTask.globalStaticAnswer || ''}
                            onChange={({ detail }) => {
                              handleUpdateChallengeTask(
                                ChallengePropAction.TASK_GLOBAL_STATIC_ANSWER,
                                task.taskNumber,
                                detail.value
                              );
                            }}
                            data-testid={`task-${task.taskNumber}-global-static-answer-input`}
                          />
                        </FormField>
                      )}
                    </ExpandableSection>
                  </Box>
                )}
              </div>
            )}
          </div>
        </SpaceBetween>
      </ExpandableSection>
      {challengeTask && challengeConfig && (
        <ConfirmModal
          visible={visibleConfirmSample}
          data-testid={`challenge-task-${task.taskNumber}__sample-fn-confirm`}
          message={t(i18nKeys.challenges.challengeDetails.messages.useSampleFunction)}
          confirmBtnLabel={t(i18nKeys.challenges.challengeDetails.yesLabels.continue)}
          onConfirm={() => {
            handleUpdateChallengeTask(
              ChallengePropAction.TASK_VALIDATION_LAMBDA,
              task.taskNumber,
              runtimeProps.sampleFunction
            );
            setVisibleConfirmSample(false);
            setShowSampleFunction(false);
          }}
          onCancel={() => setVisibleConfirmSample(false)}
        />
      )}
      {challengeTask && challengeConfig && (
        <ConfirmModal
          visible={visibleRemoveTaskConfirm}
          title={t(i18nKeys.challenges.challengeDetails.messages.removeTaskTitle)}
          message={
            <>
              {t(i18nKeys.challenges.challengeDetails.messages.removeTaskContent)} <Spinner variant="disabled" />
            </>
          }
          data-testid={`challenge-task-${task.taskNumber}__remove-confirm-modal`}
          confirmBtnLabel={t(i18nKeys.challenges.challengeDetails.yesLabels.continue)}
          disabled={isLoading}
          onConfirm={() => {
            isLoadingSet(true);
            removeTask(editedChallenge?.challengeId || '', editedChallenge?.version || 0, task.id || '')
              .catch((err) => preProdLogger(err))
              .finally(() => {
                isLoadingSet(false);
                visibleRemoveTaskConfirmSet(false);
              });
          }}
          onCancel={() => visibleRemoveTaskConfirmSet(false)}
        />
      )}
    </div>
  );
};

export default ChallengeTaskDetail;
